import React, { useState, useCallback, useEffect } from "react";
import ProgressStepper from "./components/ProgressStepper.js";
import Step0 from "./components/steps/Step0.jsx";
import Step1 from "./components/steps/Step1.jsx";
import Step2 from "./components/steps/Step2.jsx";
import Step3 from "./components/steps/Step3.jsx";
import Step4 from "./components/steps/Step4.jsx";
import Step5 from "./components/steps/Step5.jsx";
import {
  QUIZ_STEPS,
  HIDDEN_STEPS,
  STORAGE_KEYS,
  QUIZ_OPTIONS_DATA,
} from "./constants/index.js";
import { useQuizSelections } from "./hooks/useLocalStorage.js";
import { preloadNextStepImages } from "./utils/imagePreloader.js";
import { initTracking } from "./utils/tracking.js";

const STEPS_COMPONENTS = {
  welcome: Step0,
  step1: Step1,
  step2: Step2,
  step3: Step3,
  step4: Step4,
  step5: Step5,
};

const validateStepData = (step, answer) => {
  switch (step) {
    case "welcome":
      return answer && answer.trim() !== "";
    case "step1":
      return answer && answer.ethnicity && answer.age;
    case "step2":
      return answer && answer.bodyType && answer.breastSize && answer.buttSize;
    case "step3":
      return answer && answer.hairColor && answer.hairStyle && answer.eyeColor;
    case "step4":
      return answer && answer.voice && answer.voiceAudio && answer.personality;
    case "step5":
      return answer && answer.trim() !== "";
    default:
      return true;
  }
};

function SignUpPage() {
  const [currentStep, setCurrentStep] = useState(5); // Начинаем с Step5
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [selections, setSelections] = useQuizSelections();

  const handleAnswer = useCallback(
    (step, answer) => {
      if (!validateStepData(step, answer)) {
        return;
      }

      setIsTransitioning(true);

      // Save to localStorage for the new steps
      if (step === "welcome" || step.startsWith("step")) {
        const existing = JSON.parse(
          localStorage.getItem(STORAGE_KEYS.AI_GIRLFRIEND_SELECTIONS) || "{}"
        );
        const updated = { ...existing, [step]: answer };
        localStorage.setItem(
          STORAGE_KEYS.AI_GIRLFRIEND_SELECTIONS,
          JSON.stringify(updated)
        );
      }

      setSelections((prev) => ({
        ...prev,
        [step]: answer,
      }));

      // Preload images for the next step
      setTimeout(() => {
        preloadNextStepImages(step, QUIZ_OPTIONS_DATA);
      }, 100);

      setTimeout(() => {
        setCurrentStep((prev) => prev + 1);
      }, 500);
    },
    [setSelections]
  );

  const handlePrevious = useCallback(() => {
    if (isTransitioning) return;

    setIsTransitioning(true);
    setCurrentStep((prev) => (prev > 0 ? prev - 1 : 0));
  }, [isTransitioning]);

  const resetQuiz = useCallback(() => {
    setCurrentStep(0);
    setSelections({});
    setIsTransitioning(false);
    localStorage.removeItem(STORAGE_KEYS.AI_GIRLFRIEND_SELECTIONS);
  }, [setSelections]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsTransitioning(false);
    }, 100);

    return () => clearTimeout(timer);
  }, [currentStep]);

  // Initialize tracking system when app starts
  useEffect(() => {
    initTracking();
  }, []);

  const getVisibleSteps = useCallback(() => {
    return QUIZ_STEPS.filter((_, index) => !HIDDEN_STEPS.includes(index));
  }, []);

  const shouldShowProgressStepper = useCallback(() => {
    return !HIDDEN_STEPS.includes(currentStep);
  }, [currentStep]);

  const currentStepName = QUIZ_STEPS[currentStep];
  const CurrentStepComponent = STEPS_COMPONENTS[currentStepName];

  if (!CurrentStepComponent) {
    return (
      <div className="quizContentWrapper">
        <div className="quizContent">
          <h1>Step not found</h1>
          <button onClick={resetQuiz}>Reset Quiz</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`app ${isTransitioning ? "no-click" : ""}`}>
      {shouldShowProgressStepper() && (
        <ProgressStepper
          currentStep={currentStep}
          totalSteps={getVisibleSteps().length}
        />
      )}
      <CurrentStepComponent
        onAnswer={handleAnswer}
        selections={selections}
        onPrevious={handlePrevious}
        isTransitioning={isTransitioning}
      />
    </div>
  );
}

export default SignUpPage;
